import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from "@angular/core";
import { ChannelCodec, Resolution } from "../../../../domain/channel-codec";
import {
  ChannelDistribution,
  ChannelFormat,
  ChannelInfo,
  ChannelPushFlvSettings,
  ChannelPushMpgtsSettings,
  ChannelPushSrtFormat,
  ChannelPushSrtSettings,
  CustomChannelPlatform,
} from "../../../../domain/channel";
import { UserService } from "../../../../domain/user-service";
import { Font } from "../../../../domain/font";
import { ChannelRepository } from "../../../../domain/channel-repository";
import { PopOverComponent } from "../../../pop-over/pop-over.component";
import { ChannelMapper } from "../../../../infrastructure/repositories/channel/channel.mapper";
import { ICommonUtilInterface } from "../../../../domain/common/common-util.interface";
import { ToastrService } from "ngx-toastr";
import { EdgeServer } from "../../../../domain/edge-server";

@Component({
  selector: "custom-platform",
  templateUrl: "./custom-platform.component.html",
  styleUrls: ["./custom-platform.component.scss"],
})
export class CustomPlatformComponent implements OnInit {
  closeIcon = '/assets/icons/close.svg';
  isAddOrEditCodec = false;

  formatToEdit: ChannelFormat;
  codecToEdit: ChannelCodec;
  channelDistributionFormats: ChannelDistribution;

  fonts: Font[];
  languages: { name: string; code: string }[];
  @Input() allEdgeServers: EdgeServer[];
  @Input() burnSubtitlesToVideo: boolean;
  @Input() channelCodecs: ChannelCodec[];
  @Input() platform: CustomChannelPlatform;
  @Input() channelInfo: ChannelInfo;
  @Output() saveCodec = new EventEmitter<ChannelCodec>();

  @Output() edgeServerChanged = new EventEmitter<void>();

  @ViewChildren(PopOverComponent)
  "dvbSubtitleSettingsPopover": QueryList<PopOverComponent>;

  title: TemplateRef<unknown> | null = null;

  constructor(
    private userService: UserService,
    private channelRepository: ChannelRepository,
    private channelMapper: ChannelMapper,
    private _commonUtils: ICommonUtilInterface,
    private _toastr: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const currentUser = this.userService.getCurrentUser();
    this.languages = currentUser.languages;
    this.fonts = currentUser.fonts;

    this.channelRepository
      .getChannelDistributionOptions()
      .then((channelOptions: ChannelDistribution) => {
        this.channelDistributionFormats =
          this.channelMapper.mapChannelDistributionOption(channelOptions);
        this.formatToEdit = this.channelDistributionFormats.getFormat(
          this.platform.pushSettings.format,
        );
        console.log(
          "custom-platform->ngOnInit",
          this.platform.pushSettings.format,
        );
      });
  }

  editCodec(codec, pushFormat: string) {
    this.formatToEdit = this.channelDistributionFormats.getFormat(pushFormat);
    this.codecToEdit = new ChannelCodec(
      codec.id,
      codec.name,
      codec.isDefault,
      {
        codecType: codec.video.codecType,
        resolution: new Resolution(
          codec.video.resolution.width,
          codec.video.resolution.height,
        ),
        fps: codec.video.fps,
        profile: codec.video.profile,
        bitrate: codec.video.bitrate,
        gopSize: codec.video.gopSize,
        interlaceType: codec.video.interlaceType,
        muxOverhead: codec.video.muxOverhead,
        specialOptions: codec.video.specialOptions,
        colourSpace: codec.video.colourSpace,
        mpegColourRange: codec.video.mpegColourRange,
      },
      {
        codecType: codec.audio.codecType,
        channels: codec.audio.channels,
        samplerate: codec.audio.samplerate,
        bitrate: codec.audio.bitrate,
        specialOptions: codec.audio.specialOptions,
      },
    );
    this.isAddOrEditCodec = true;
  }
  public get isChannelPushSrtFormat(): boolean {
    return this.formatToEdit instanceof ChannelPushSrtFormat;
  }

  addNewCodec() {
    this.formatToEdit = this.channelDistributionFormats.getFormat(
      this.platform.pushSettings.format,
    );
    this.codecToEdit = new ChannelCodec(
      undefined,
      "Untitled Codec",
      false,
      {
        codecType: this.formatToEdit.defaultSettings.videoCodecType,
        resolution:
          this.formatToEdit.defaultSettings.videoCodecResolution.duplicate() as Resolution,
        fps: this.formatToEdit.defaultSettings.videoCodecFPS,
        profile: this.formatToEdit.defaultSettings.videoCodecProfile.id,
        bitrate: this.formatToEdit.defaultSettings.videoCodecBitrate,
        gopSize: this.formatToEdit.defaultSettings.videoCodecGOPSize,
        interlaceType:
          this.formatToEdit.defaultSettings.videoCodecProgressive.id,
        muxOverhead: this.formatToEdit.defaultSettings.videoCodecMUX,
        specialOptions:
          this.formatToEdit.defaultSettings.videoCodecSpecialOptions,
        colourSpace: this.formatToEdit.defaultSettings.colourSpace,
        mpegColourRange: this.formatToEdit.defaultSettings.mpegColourRange,
      },
      {
        codecType: this.formatToEdit.defaultSettings.audioCodecType,
        channels: this.formatToEdit.defaultSettings.audioCodecChannels.channels,
        samplerate: this.formatToEdit.defaultSettings.audioCodecSampleRate,
        bitrate: this.formatToEdit.defaultSettings.audioCodecBitrate,
        specialOptions:
          this.formatToEdit.defaultSettings.audioCodecSpecialOptions,
      },
    );

    this.isAddOrEditCodec = true;
    if (!this.formatToEdit) {
      this.formatToEdit = this.channelDistributionFormats.getFormat("mpegts");
    }
  }

  onCodecSave(codec) {
    this.saveCodec.emit(codec);
    this.isAddOrEditCodec = false;
  }

  trackById(index, item) {
    return item.id;
  }

  onSettingsClicked(): void {}

  getDvbSubtitleLanguages(): string[] {
    return Object.keys(this.platform.dvbSubtitles);
  }

  onDvbSubtitleLanguagesChange(codes: string[]) {
    const dvbSubtitles: {
      [languageCode: string]: {
        fontId: number;
        size: number;
        subtitleStartPositionFromTop: number;
        subtitleEndPositionFromTop: number;
      };
    } = {};
    codes.forEach((code) => {
      dvbSubtitles[code] = this.platform.dvbSubtitles[code] || {
        fontId: this.fonts[0].id,
        size: 12,
        subtitleStartPositionFromTop: 75,
        subtitleEndPositionFromTop: 90,
      };
    });
    this.platform.dvbSubtitles = dvbSubtitles;
  }

  async onChannelFormatChange(format: ChannelFormat) {
    if (this.platform.pushSettings.format !== this.formatToEdit.name) {
      if (format.name === "SRT") {
        const randomPortNum = await this._getRandomFreeSrtPort();
        const passPhrase = this._commonUtils.generateText(10);
        this.platform.pushSettings = new ChannelPushSrtSettings(
          null,
          false,
          randomPortNum,
          "",
          passPhrase,
          3000,
        );
      } else if (format.name === "flv") {
        this.platform.pushSettings = new ChannelPushFlvSettings(
          null,
          format.exampleUrl,
        );
      } else {
        this.platform.pushSettings = new ChannelPushMpgtsSettings(
          null,
          format.exampleUrl,
        );
      }
    }
    this.formatToEdit = format;

    this.changeDetectorRef.markForCheck();
  }

  getFormatUrlPlaceholder(_event?: unknown): string {
    if (!this.channelDistributionFormats) {
      return "";
    }

    const format: ChannelFormat = this.channelDistributionFormats.getFormat(
      this.platform.pushSettings.format,
    );
    return format ? format.exampleUrl : "";
  }

  isSubtitleLanguageChecked(code) {
    return !!this.platform.dvbSubtitles[code];
  }

  onSubtitleSettingsSave(
    settingsPopover,
    languageCode,
    fontId,
    subtitleStartPositionFromTop,
    subtitleEndPositionFromTop,
  ) {
    this.platform.dvbSubtitles[languageCode] = {
      fontId: fontId,
      size: 12,
      subtitleStartPositionFromTop: subtitleStartPositionFromTop,
      subtitleEndPositionFromTop: subtitleEndPositionFromTop,
    };
    settingsPopover.hide();
  }

  onLanguageSettingsClicked({ event, item }) {
    console.log(event, item);
    this.dvbSubtitleSettingsPopover.forEach((i) => i.hide());
    const langs = this.dvbSubtitleSettingsPopover.filter(
      (x) => x.contentClass === `lang-code-${item.value}`,
    );
    if (langs.length > 0) {
      langs[0].toggle(event);
    }
  }

  async srtPortNumberChanged(event: any) {
    const pushSrtSettings = this.platform
      .pushSettings as ChannelPushSrtSettings;
    let maxPortNum = 65535;
    let minPortNum = 0;
    if (pushSrtSettings.pushMode === false) {
      maxPortNum = 50000;
      minPortNum = 40000;
    }
    const newVal = parseInt(event.target.value);
    const hasAnotherPlatformWithSamePort =
      this.channelInfo.platforms.filter(
        (p) =>
          p !== this.platform &&
          (p as CustomChannelPlatform).pushSettings instanceof
            ChannelPushSrtSettings &&
          ((p as CustomChannelPlatform).pushSettings as ChannelPushSrtSettings)
            .portNum === newVal,
      ).length > 0;

    if (
      !newVal ||
      newVal > maxPortNum ||
      newVal < minPortNum ||
      hasAnotherPlatformWithSamePort ||
      !(await this.channelRepository.checkIfSrtPortIsFree(
        this.channelInfo.id,
        newVal,
      ))
    ) {
      this._toastr.error(
        `Chosen port is not in range or busy. Generating a random one...`,
      );
      pushSrtSettings.portNum = await this._getRandomFreeSrtPort();
    }
    this.changeDetectorRef.markForCheck();
  }

  onLatencyMsChanged(event: any) {
    const newValue = event.target.value;
    if (!newValue) {
      (this.platform.pushSettings as ChannelPushSrtSettings).latencyMs = 3000;
    }
  }

  private async _getRandomFreeSrtPort(): Promise<number> {
    const randomNumber = this._commonUtils.generateInteger(40000, 50000);
    const hasAnotherPlatformWithSamePort =
      this.channelInfo.platforms.filter(
        (p) =>
          p !== this.platform &&
          (p as CustomChannelPlatform).pushSettings instanceof
            ChannelPushSrtSettings &&
          ((p as CustomChannelPlatform).pushSettings as ChannelPushSrtSettings)
            .portNum === randomNumber,
      ).length > 0;
    if (
      (await this.channelRepository.checkIfSrtPortIsFree(
        this.channelInfo.id,
        randomNumber,
      )) === true &&
      hasAnotherPlatformWithSamePort === false
    ) {
      return randomNumber;
    } else {
      return this._getRandomFreeSrtPort();
    }
  }

  onPassPhraseChanged(event: any) {
    const newValue = event.target.value;
    if (newValue.length > 0 && (newValue.length > 80 || newValue.length < 10)) {
      this._toastr.error(
        `Chosen Pass Phrase must be between 10 and 80 characters. Generating a random one...`,
      );
      (this.platform.pushSettings as ChannelPushSrtSettings).passPhrase =
        this._commonUtils.generateText(10);
    }
  }

  onEdgeServerChanged() {
    this.edgeServerChanged.emit();
  }
}
